/**
 * Core Accounting Types for SponsFi Platform
 */

import { Decimal } from 'decimal.js';
import { QueryResult } from 'pg';
import type {
  JournalEntry as ImportedJournalEntry,
  JournalEntryType as ImportedJournalEntryType,
  RecurringSchedule as ImportedRecurringSchedule,
  RenewalInfo as ImportedRenewalInfo,
  TransactionTemplate as ImportedTransactionTemplate
} from './journalEntry';
import { JournalEntryData } from '../hooks/useJournalEntries';

// Rename imports to avoid conflicts with local declarations
export type {
  ImportedJournalEntry as JournalEntryFromJournal,
  ImportedJournalEntryType as JournalEntryTypeFromJournal,
  ImportedRecurringSchedule as RecurringScheduleFromJournal,
  ImportedRenewalInfo as RenewalInfoFromJournal,
  ImportedTransactionTemplate as TransactionTemplateFromJournal
};

// Direct interface exports
export interface AccountCategory {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: AccountType;
  subcategories?: AccountSubcategory[];
  parentId?: string;
}

export interface AccountSubcategory {
  id: string;
  code: string;
  name: string;
  description?: string;
  parentId: string;
}

export interface CategoryHierarchy {
  category: string;
  subcategories: string[];
  parentCategory?: string;
}

export interface JournalEntryFormProps {
  onSubmit: (data: JournalEntry) => Promise<void>;
  onCancel?: () => void;
  initialData?: Partial<JournalEntry>;
  isLoading?: boolean;
}

export interface AccountingCategorizationValues {
  accountCategory: string;
  accountSubcategory: string;
  functionType: string;
  form990Category: string;
  fiscalSponsorshipType: string;
  form990Subcategory: string;
  accountingCode: string;
}

export interface CategorizationState {
  accountCategory: string;
  accountSubcategory: string;
  accountingCode: string;
  functionType: string;
  form990Category: string;
  form990Subcategory: string;
  fundRestrictionType: string;
  fiscalSponsorshipType: string;
  allocationPercentages: {
    program: number;
    management: number;
    fundraising: number;
  };
  grantAllocation?: {
    directCost: number;
    indirectCost: number;
    isFederalGrant: boolean;
  };
}

export interface AccountingCategorizationState extends CategorizationState {
  lineItemDescription: string;
  selectedAccount: string;
  amount: number;
  expenseType: string;
  category: string;
  contractType: string;
  allocationMethod: string;
  costCenter: string;
}

export interface CreateAccountDTO {
  code: string;
  name: string;
  type: AccountType;
  category: AccountCategory;
  description?: string;
  isContra: boolean;
  parentId?: string;
}

// Enum exports
export enum AccountType {
  ASSET = 'asset',
  LIABILITY = 'liability',
  EQUITY = 'equity',
  REVENUE = 'revenue',
  EXPENSE = 'expense'
}

export enum AccountSpecialType {
  BANK = 'bank',
  CREDIT_CARD = 'credit_card',
  INVESTMENT = 'investment',
  PAYROLL = 'payroll',
  TAX = 'tax'
}

export enum BalanceType {
  DEBIT = 'debit',
  CREDIT = 'credit'
}

export enum FundType {
  OPERATING = 'operating',
  ENDOWMENT = 'endowment',
  CAPITAL = 'capital'
}

export enum RestrictionType {
  UNRESTRICTED = 'unrestricted',
  TEMPORARILY_RESTRICTED = 'temporarily_restricted',
  PERMANENTLY_RESTRICTED = 'permanently_restricted'
}

// Constant exports
export const accountingStyles = {
  fontSize: '14px',
  fontFamily: 'monospace',
  color: '#333',
  borderColor: '#ddd'
} as const;

export interface AccountFormData {
  code: string;
  name: string;
  type: AccountType;
  specialAccountType?: AccountSpecialType;
  description?: string;
  category: string;
  taxCategory: TaxCategory;
  isRestricted: boolean;
  fundType: FundType;
  allowsDirectDonations: boolean;
  status: 'active' | 'inactive';
  accountCode?: string;
  taxCode?: string;
  restrictionDetails?: string;
}

export interface Account {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  description?: string;
  category: string;
  taxCategory: TaxCategory;
  isRestricted: boolean;
  fundType: FundType;
  allowsDirectDonations: boolean;
  parent_id?: string;
  balance?: number;
  status: 'active' | 'inactive';
  normalBalance: 'DEBIT' | 'CREDIT';
  metadata?: {
    [key: string]: any;
  };
  specialAccountType?: AccountSpecialType;
  accountCode?: string;
  taxCode?: string;
  restrictionDetails?: string;
}

export interface ExtendedAccount extends Account {
  isActive: boolean;
  hasChildren: boolean;
}

export interface AccountFormErrors {
  code?: string;
  name?: string;
  type?: string;
  category?: string;
  taxCategory?: string;
  fundType?: string;
  specialAccountType?: string;
  accountCode?: string;
  taxCode?: string;
  restrictionDetails?: string;
}

export type JournalEntryType = 'standard' | 'adjustment' | 'closing';

export interface LineItem {
  id: string;
  accountId: string;
  description: string;
  debit: number;
  credit: number;
  fundType?: FundType;
  restrictionType?: RestrictionType;
  taxCategory?: string;
  metadata?: Record<string, unknown>;
}

export interface AccountingCategorization {
  accountCategory: string;
  accountSubcategory?: string;
  functionType?: string;
  form990Category?: string;
  form990Subcategory?: string;
  fiscalSponsorshipType?: string;
  accountingCode?: string;
  metadata?: Record<string, unknown>;
}

export interface JournalLineItem {
  id?: string;
  accountId: string;
  fundId?: string;
  debit?: number;
  credit?: number;
  memo?: string;
  metadata?: Record<string, any>;
}

export interface Journal {
  id: string;
  fiscal_sponsor_id: string;
  cause_id?: string;
  entry_date: Date;
  posted_date?: Date;
  reference_number?: string;
  description?: string;
  status: 'draft' | 'posted' | 'void';
  created_at: Date;
  updated_at: Date;
}

export interface Fund {
  id: string;
  name: string;
  code: string;
  type: string;
  restriction_type: string;
  description?: string;
  start_date?: Date;
  end_date?: Date;
  created_at: Date;
  updated_at: Date;
  status: 'active' | 'inactive' | 'pending';
  restrictions: FundRestriction[];
  grant_requirements: GrantRequirement[];
}

export interface FundRestriction {
  id: string;
  type: string;
  description: string;
  conditions: any[];
  start_date?: Date;
  end_date?: Date;
  status: 'active' | 'inactive';
  priority: number;
}

export interface GrantRequirement {
  id: string;
  type: string;
  description: string;
  due_date: Date;
  reporting_frequency: 'annual' | 'semi-annual' | 'quarterly' | 'monthly';
  compliance_requirements: ComplianceRequirement[];
  budget_constraints: BudgetConstraint[];
  status: 'pending' | 'active' | 'completed' | 'overdue';
  notifications: number[]; // Days before due date to send notifications
}

export interface Transaction {
  id: string;
  fiscalSponsorId: string;
  causeId?: string;
  projectId?: string;
  grantId?: string;
  date: Date;
  postDate?: Date;
  type: 'standard' | 'adjustment' | 'transfer' | 'reversal';
  status: 'draft' | 'pending' | 'approved' | 'posted' | 'rejected';
  entries: TransactionEntry[];
  metadata: Record<string, any>;
  approvals?: Array<{
    userId: string;
    date: Date;
    status: 'approved' | 'rejected';
    notes?: string;
  }>;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TransactionEntry {
  id?: string;
  transactionId?: string;
  accountId: string;
  date: Date;
  debit?: Decimal | number;
  credit?: Decimal | number;
  memo?: string;
  fundId?: string;
  projectCode?: string;
  grantCode?: string;
  restrictions?: string[];
  tags?: string[];
  customFields?: Record<string, any>;
}

export interface AccountBalance {
  accountId: string;
  fiscalYear: number;
  period: number;
  balance: Decimal;
  lastUpdated: Date;
}

export interface RestrictionDetail {
  id: string;
  type: RestrictionType;
  description: string;
  amount: number;
  startDate: Date;
  endDate?: Date;
  conditions: string[];
  metadata: Record<string, any>;
}

export interface AllocationDetail {
  id: string;
  purpose: string;
  amount: number;
  startDate: Date;
  endDate?: Date;
  status: 'active' | 'completed' | 'cancelled';
  metadata: Record<string, any>;
}

export interface FundBalance {
  total_balance: number;
  available_balance: number;
  pending_balance: number;
  restricted_balance: number;
  last_updated: Date;
  currency: string;
  breakdown: {
    assets: number;
    liabilities: number;
    equity: number;
    reserved: number;
  };
  restrictions?: {
    type: string;
    amount: number;
    description: string;
  }[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: Array<{
    field: string;
    message: string;
  }>;
}

export interface FiscalPeriod {
  fiscalYear: number;
  period: number;
  startDate: Date;
  endDate: Date;
  status: 'open' | 'closed' | 'adjusting';
}

export interface AICategory {
  category: string;
  confidence: number;
  suggestedTags?: string[];
  metadata?: Record<string, any>;
}

export interface TransactionContext {
  fiscalSponsorId: string;
  userId: string;
  options?: {
    autoCategorizationEnabled?: boolean;
    dryRun?: boolean;
  };
}

export type TransactionType = 'standard' | 'adjustment' | 'transfer' | 'reversal';
export type TransactionStatus = 'pending' | 'approved' | 'rejected' | 'posted';

export interface FiscalSponsor {
  id: string;
  name: string;
  ein: string;
  status: 'active' | 'inactive' | 'suspended';
  contactInfo: {
    address: string;
    phone: string;
    email: string;
  };
  bankingInfo: {
    routingNumber: string;
    accountNumber: string;
    bankName: string;
  };
  settings: Record<string, any>;
  metadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Cause {
  id: string;
  fiscalSponsorId: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  metadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Report {
  id: string;
  fiscalSponsorId: string;
  type: string;
  data: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface ChartAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  children?: ChartAccount[];
  balance?: number;
}

// Form Types
export interface AccountFormProps {
  account?: Account | null;
  onSubmit: (data: Partial<Account>) => void | Promise<void>;
  onCancel: () => void;
  accounts?: ExtendedAccount[];
  chartOfAccounts?: any;
}

// Service Types
export interface AccountService {
  getAccounts: (filters?: AccountFilters) => Promise<Account[]>;
  createAccount: (fiscalSponsorId: string, account: Partial<Account>) => Promise<Account>;
  updateAccount: (accountId: string, account: Partial<Account>) => Promise<Account>;
  deleteAccount: (accountId: string) => Promise<void>;
}

export interface AccountFilters {
  type?: string;
  category?: string;
  search?: string;
  showInactive?: boolean;
  taxCategory?: string;
  fundType?: string;
}

// IRS Types
export interface IRSCategory {
  name: string;
  subcategories: Record<string, string>;
}

export interface IRSCategoryGroup {
  code: string;
  categories: Record<string, IRSCategory>;
}

export interface AccountDetailsDrawerProps {
  account: Account | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (accountId: string, accountData: Partial<Account>) => Promise<void>;
  showTaxCategories?: boolean;
  showFundTypes?: boolean;
  showRestrictions?: boolean;
  onEdit?: () => void;
}

export interface AdvancedFiltersProps {
  filters: AccountFilters;
  onFilterChange: (filters: AccountFilters) => void;
  showTaxCategories?: boolean;
  showFundTypes?: boolean;
}

export interface AccountingPeriod {
  startDate: string;
  endDate: string;
}

export interface Transaction {
  id: string;
  amount: number;
  transaction_date: string;
  account_type: string;
  transaction_type: 'REVENUE' | 'EXPENSE';
  description: string;
  category: string;
  subcategory?: string;
}

export interface Organization {
  id: string;
  ein: string;
  legal_name: string;
  dba_name?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  };
  fiscal_year_start: Date;
  fiscal_year_end: Date;
  website?: string;
  formation_type: string;
  formation_date: string | Date;
  has_collections?: boolean;
}

export interface JournalEntryState {
  // Header & Metadata
  entryId: string;
  date: Date;
  fundCategory: string;
  transactionType: string;
  approvalStatus: string;
  attachments: File[];

  // Primary Classification
  accountCategory: string;
  subcategory: string;
  accountCode: string;
  costCenter: string;
  fundType: string;

  // Fund & Grant
  grantType: string;
  grantCategory: string;
  indirectCostRate: string;
  projectCode: string;
  fiscalSponsorshipModel: string;

  // Transaction Specific
  expenseType?: string;
  vendorCategory?: string;
  contractType?: string;
  revenueType?: string;
  specialEventCode?: string;
  fairMarketValue?: number;

  // Allocation & Compliance
  irs990Category: string;
  functionalClass: string;
  auditClass: string;
  isLobbyingExpense: boolean;
  
  // Multi-tagging & Splits
  isSplitTransaction: boolean;
  allocations: Array<{
    department: string;
    percentage: number;
  }>;
  isRecurring: boolean;
  needsBoardApproval: boolean;
}

export type TaxCategory = 
  | 'GENERAL'
  | 'PROGRAM'
  | 'FUNDRAISING'
  | 'ADMINISTRATIVE'
  | 'RESTRICTED';

export interface AccountCodeGenerationParams {
  type: AccountSpecialType;
  category: string;
  entityType: string;
}

export interface BalanceSnapshot {
  asOfDate: Date;
  balances: {
    unrestricted: Decimal;
    temporarilyRestricted: Decimal;
    permanentlyRestricted: Decimal;
    total: Decimal;
  };
  restrictions: RestrictionDetail[];
  allocations: AllocationDetail[];
}

export interface ValidationRules {
  type: 'amount' | 'percentage' | 'date' | 'status';
  operator: '>' | '<' | '=' | '>=' | '<=';
  amount?: number;
  threshold?: number;
  date?: Date;
  status?: string;
  document_type?: string;
}

export interface ComplianceRequirement {
  id: string;
  type: 'documentation' | 'financial' | 'reporting';
  description: string;
  required: boolean;
  validation_rules: ValidationRules;
  status?: 'active' | 'inactive';
  due_date?: Date;
}

export interface BudgetConstraint {
  id: string;
  category: string;
  min_amount?: number;
  max_amount?: number;
  percentage?: number;
  description: string;
}

export interface FinancialSummary {
  total: number;
  categories: {
    [key: string]: number;
  };
  subcategories: {
    [key: string]: {
      [key: string]: number;
    };
  };
}

export interface BalanceResponse {
  status: 'success' | 'error';
  data: FundBalance;
  timestamp: Date;
}

export interface FiscalYear {
  fiscal_year_start: Date;
  fiscal_year_end: Date;
  tax_year: number;
  fund_id: string;
  status: 'active' | 'closed';
}

export interface FundTotals {
  [category: string]: number;
}

export interface PaymentRequestVendor {
  id: string;
  name: string;
  w9Status: 'verified' | 'pending' | 'expired' | 'missing';
  ofacStatus: 'clear' | 'review' | 'blocked';
}

export interface PaymentRequest {
  id: string;
  requestDate: Date;
  vendor: PaymentRequestVendor;
  amount: number;
  description: string;
  category: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  documents: Array<{
    id: string;
    type: string;
    name: string;
    url: string;
    verified: boolean;
  }>;
  budget: {
    lineItem: string;
    remaining: number;
    totalAllocated: number;
  };
  compliance: {
    checks: Array<{
      type: string;
      status: 'pass' | 'fail' | 'warning';
      message: string;
    }>;
    restrictions: Array<{
      type: string;
      description: string;
      impact: 'blocked' | 'warning' | 'none';
    }>;
  };
  approvals: Array<{
    level: string;
    approver: string;
    date: Date;
    notes: string;
  }>;
  history: Array<{
    date: Date;
    action: string;
    user: string;
    notes: string;
  }>;
  fundName: string;
}

export interface ReconciliationTransaction {
  id: string;
  date: Date;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  status: 'pending' | 'reconciled' | 'disputed';
  category: string;
  reference_number?: string;
  bank_details?: {
    account_number: string;
    transaction_id: string;
    statement_date: Date;
  };
  metadata?: Record<string, any>;
  reconciliation_notes?: string;
  matched_entries?: {
    entry_id: string;
    match_confidence: number;
  }[];
}

export interface ValidationResults {
  errors: string[];
  warnings: string[];
}

export interface ChartAccountEntry {
  code: string;
  name: string;
  subcategories?: {
    code: string;
    name: string;
  }[];
}

export interface ChartOfAccountsType {
  Assets: ChartAccountEntry[];
  Liabilities: ChartAccountEntry[];
  "Net Assets": ChartAccountEntry[];
  Revenue: ChartAccountEntry[];
  Expenses: {
    Program: ChartAccountEntry[];
    Administrative: ChartAccountEntry[];
    Fundraising: ChartAccountEntry[];
  };
}

export interface SmartSuggestion {
  accountId: string;
  description: string;
  amount: number;
  taxCategory?: string;
  metadata?: Record<string, unknown>;
}

export interface FundRestriction {
  fundId: string;
  restrictionType: 'time' | 'purpose' | 'both' | 'none';
  startDate?: Date;
  endDate?: Date;
  purpose?: string;
  allowedCategories?: string[];
  disallowedCategories?: string[];
  spendingLimit?: number;
  currentBalance?: number;
}

export interface JournalTemplate {
  id: string;
  type: 'standard' | 'adjustment' | 'closing';
  name: string;
  description: string;
  lineItems: {
    accountId: string;
    description: string;
    debit?: number;
    credit?: number;
    taxCategory: string;
    fundRestriction?: string;
  }[];
}

export interface FundingAllocation {
  id?: string;
  fundId: string;
  sourceId: string;
  sourceType: 'grant' | 'program' | 'campaign' | 'general' | 'journal' | 'lineItem';
  amount: number;
  percentage: number;
  restrictionType: RestrictionType;
}

// Keep both types if needed elsewhere
export type FundingAllocationInput = FundingAllocation;

export interface ContextualHelp {
  code: string;
  description: string;
  examples: string[];
  restrictions?: string[];
  relatedCodes?: string[];
}

export interface ScheduleState {
  isScheduledPayment: boolean;
  scheduledPaymentDate: Date | null;
  hasRenewalReminder: boolean;
  isRecurring: boolean;
  recurringSchedule: {
    frequency: 'monthly' | 'quarterly' | 'annually';
    startDate: Date;
    endDate: Date | null;
  };
  renewalInfo: {
    type: 'subscription' | 'contract' | 'license';
    nextRenewalDate: Date | null;
    notifyBefore: number;
  };
}

export interface RecurringSchedule {
  frequency: 'monthly' | 'daily' | 'weekly' | 'yearly';
  startDate: Date;
  endDate: Date | null;
}

export interface RenewalInfo {
  type: 'subscription' | 'contract' | 'license';
  nextRenewalDate: Date | null;
  notifyBefore: number;
  autoRenew: boolean;
  renewalDate: Date | null;
  reminderDays: number[];
  contractTerm: string;
  termType: 'months' | 'years';
  notes: string;
}

export interface TransactionTemplate {
  id: string;
  name: string;
  type: JournalEntryType;
  description: string;
  // ... other template properties
}

export interface JournalEntry extends Omit<JournalEntryData, 'type'> {
  type: JournalEntryType;
  // ... other specific JournalEntry properties ...
}

